import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Paid Boost tier picker.
 * Spec: SPEC_PAID_BOOST_ALL_PODS (iter 205).
 * GET /classifieds/boost/options → 3 tiers (7/14/30 days at $5/$9/$15 NZD).
 * POST /classifieds/listings/{id}/boost/checkout → Stripe URL redirect.
 * After Stripe returns, /boost/return polls GET /boost/status/{session_id}.
 */
export default function BoostDialog({ open, onOpenChange, listing }) {
  const optionsQ = useQuery({
    queryKey: ["boost-options"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/boost/options");
      return data;
    },
    enabled: open,
    staleTime: 30 * 60_000,
    retry: 0,
  });

  const checkoutMut = useMutation({
    mutationFn: async (tier) => {
      const originUrl = `${window.location.origin}/boost/return`;
      const { data } = await sharedApi.post(
        `/classifieds/listings/${listing.id}/boost/checkout`,
        { tier, origin_url: originUrl },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        // Persist which ad we boosted so the return page can show a nice message
        try {
          sessionStorage.setItem(
            "boost_pending",
            JSON.stringify({ listing_id: listing.id, title: listing.title, session_id: data.session_id }),
          );
        } catch { /* ignore */ }
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start Stripe checkout — please try again.");
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Boost checkout failed"),
  });

  const tiers = optionsQ.data?.tiers || [];
  const currency = optionsQ.data?.currency || "NZD";
  const note = optionsQ.data?.note;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="boost-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[color:var(--brand-orange)]" />
            Feature on the homepage
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Pay once — your ad floats to the top of the homepage and search results for the whole period.
          </DialogDescription>
        </DialogHeader>

        {optionsQ.isLoading && (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {!optionsQ.isLoading && optionsQ.isError && (
          <div className="py-8 text-center">
            <div className="text-sm text-slate-600">Couldn&apos;t load boost options.</div>
            <button
              type="button"
              onClick={() => optionsQ.refetch()}
              className="mt-3 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {tiers.length > 0 && (
          <div className="mt-2 grid md:grid-cols-3 gap-3" data-testid="boost-tiers">
            {tiers.map((t, i) => {
              const isMiddle = i === 1;
              return (
                <button
                  key={t.tier}
                  type="button"
                  onClick={() => checkoutMut.mutate(t.tier)}
                  disabled={checkoutMut.isPending}
                  data-testid={`boost-tier-${t.tier}`}
                  className={`text-left rounded-2xl p-5 border-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-0.5 ${
                    isMiddle
                      ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange)]/5 shadow-card"
                      : "border-soft bg-white hover:border-[color:var(--brand-blue)]"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t.days} days</div>
                    {isMiddle && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-orange)]">Best value</span>
                    )}
                  </div>
                  <div className="mt-2 font-display text-3xl font-black text-slate-900">
                    ${formatPrice(t.price)}
                    <span className="text-sm font-semibold text-slate-500 ml-1">{currency}</span>
                  </div>
                  <div className="mt-1 font-semibold text-slate-700 text-sm">{t.name}</div>
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> Homepage featured rail
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> Floats to top of search
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> Extends any existing boost
                    </li>
                  </ul>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[color:var(--brand-blue)]">
                    {checkoutMut.isPending && checkoutMut.variables === t.tier ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                      </>
                    ) : (
                      <>Pay ${formatPrice(t.price)} →</>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {note && (
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">{note}</p>
        )}
        <p className="mt-1 text-[11px] text-slate-400">
          Payments are processed by Stripe in test mode — use card 4242 4242 4242 4242 with any future expiry &amp; CVC.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function formatPrice(n) {
  if (!Number.isFinite(Number(n))) return "0";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
}
